import { useFormik } from "formik";
import Box from "@material-ui/core/Box";
import MaskedInput from "react-text-mask";
import DatePicker from "react-datepicker";
import { toastr } from "react-redux-toastr";
import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import CircularProgress from "@mui/material/CircularProgress";
import { Dialog, Typography, DialogContent } from "@material-ui/core";
// component
import SwitchComp from "components/switch";
import MyButton from "components/MyButton/index";
import Select, { components } from "react-select";
import PastoralService from "Lib/API/PastoralApi";
import { LabelField, TextareaField } from "components";
import { getDateFormat, maskDate } from "helper/dateFormat";
import BottomArrowSvg from "assets/icons/svg/BottomArrowSvg";
import { getDateFormatForDatePicket } from "helper/dateFormat";
import { MultiSelect } from "components/MultiSelect/MultiSelect";
import NewEventSuccess from "pages/EasyCare/newEvent/NewEventSuccess";
import createAutoCorrectedDatePipe from "text-mask-addons/dist/createAutoCorrectedDatePipe";
// direct import
import { renderFormikError } from "./helper";
import { initialValues, selectControlStyles, validationSchema } from "./helper";

const formatOptionLabel = ({ label, _id }) => (
  <Box className="dropdown-container" key={_id}>
    <Box className="icon-name-container d-flex align-items-center justify-content-start">
      <Box className="name-container" style={{ marginInlineStart: 10 }}>
        <Box>{label}</Box>
      </Box>
    </Box>
  </Box>
);

const colorStyles = {
  control: (styles) => ({
    ...styles,
    ...selectControlStyles,
  }),
  menuList: (styles) => ({
    ...styles,
    maxHeight: "150px",
  }),
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <BottomArrowSvg label="Emoji" />
  </components.DropdownIndicator>
);

const NewEvent = ({
  open,
  closeModal,
  getEventList,
  fetchMyAPI,
  setPage,
  members,
  loadingMembers,
  eventTypes,
  loadingEventTypes,
  closeEditModal,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const userContactId = localStorage.getItem("user_contact_id")
    ? JSON.parse(localStorage.getItem("user_contact_id"))
    : null;
  // state
  const [successFull, setSuccessFull] = useState(false);
  const [notifyToUsers, setNotifyToUsers] = useState([]);
  const [successEventId, setSuccessEventId] = useState(null);
  const [successEventData, setSuccessEventData] = useState([]);
  const [loadingNotifyTo, setLoadingNotifyTo] = useState(false);
  const [updateEventLoading, setUpdateEventLoading] = useState(false);

  const dateMask = maskDate();
  const placeholderFormat = getDateFormat();
  const dateFormat = getDateFormatForDatePicket();
  const autoCorrectedDatePipe = createAutoCorrectedDatePipe(dateFormat);

  // Fetch members, event types, and notifyTo users when the component mounts
  useEffect(() => {
    fetchNotifyToUsers(1);
  }, []);

  const fetchNotifyToUsers = async (page) => {
    setLoadingNotifyTo(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await PastoralService.getEventUsers({
        token: token,
        page,
      });

      const formattedUserData = res?.users
        ?.filter((item) => item.easy_contact_id !== userContactId)
        .map((item) => {
          return {
            title: `${item?.type} ${
              item?.fullName ? "- " + item?.fullName : ""
            }`,
            id: item?.id,
            easyorg: item?.easyorg,
            easy_contact_id: item?.easy_contact_id,
          };
        });
      // setTotalUSers(res?.total)
      setNotifyToUsers(formattedUserData);
    } catch (error) {
      console.error("Error fetching notify-to users:", error);
    } finally {
      setLoadingNotifyTo(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    onSubmit: async (data) => {
      const formattedNotifyTo = (list) => {
        return list?.map((item) => {
          return {
            id: item?.id?.toString(),
            easyorg: item?.easyorg,
            easy_contact_id: item?.easy_contact_id,
          };
        });
      };

      const token = await getAccessTokenSilently();
      const notifyTo = formik.values?.account.notifyTo?.length
        ? formik.values?.account.notifyTo?.[0] === "-1"
          ? formattedNotifyTo(notifyToUsers)
          : formattedNotifyTo(formik.values?.account?.notifyTo)
        : undefined;

      const input = {
        memberId: formik.values?.memberName?.id.toString(),
        eventTypeId: formik.values?.eventType?.id.toString(),
        confidentialEvent: formik.values.isEventConfidencial ?? false,
        confidentialInfo: formik.values.isDescriptionConfidencial ?? false,
        description: formik.values.description,
        date: formik.values.eventDate,
        notifyTo,
      };
      await PastoralService.createEvent({ data: input, token })
        .then((res) => {
          setSuccessFull(true);
          setSuccessEventData(res?.data);
          setSuccessEventId(res?.eventId);
          getEventList();
          fetchMyAPI(1);
          setPage(1);
        })
        .catch((err) => {
          toastr.error("Error", err?.data?.message || "Something went wrong");
        });
    },
  });

  const { isSubmitting, setFieldValue, handleSubmit, values } = formik;

  const convertUTCToLocalDate = (date) => {
    if (!date) return date;
    date = new Date(date);
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
  };

  const convertLocalToUTCDate = (date, name) => {
    if (!date) return;
    const d = new Date(date);
    const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    setFieldValue(name, utc);
  };

  const doNotGenerateHandler = async () => {
    const token = await getAccessTokenSilently();
    setUpdateEventLoading(true);
    PastoralService.updateEvent({
      eventId: successEventId,
      token,
    })
      .then((res) => {
        getEventList();
        fetchMyAPI(1);
        setPage(1);
        toastr.success("The Event Created Successfully");
        closeEditModal();
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setUpdateEventLoading(false);
      });
  };

  const handleFormikChange = (items, field) => setFieldValue(field, items);

  return (
    <Dialog
      open={open}
      onClose={() => (!successFull ? closeModal() : closeEditModal())}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        {!successFull ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                color: "#575757",
              }}
            >
              <Typography variant="h5">Add Event</Typography>
            </Box>
            <Box className="addEvent-form-form d-flex flex-column justify-content-between h-100">
              <form onSubmit={handleSubmit}>
                <Box className="row mt-10">
                  <Box className="form-group Account-name-newEvent col-12 col-md-6">
                    <LabelField title="Member Name" mandatory />
                    <Select
                      classNamePrefix="react-select"
                      formatOptionLabel={formatOptionLabel}
                      options={members}
                      styles={colorStyles}
                      isLoading={loadingMembers}
                      onChange={(val) =>
                        setFieldValue("memberName", {
                          id: val.value,
                          name: val.label,
                        })
                      }
                      value={{
                        label: values?.memberName?.name,
                        value: values?.memberName?.id,
                      }}
                      components={{ DropdownIndicator }}
                    />
                    {renderFormikError(formik, "memberName.id")}
                  </Box>

                  <Box className="form-group event-type-newEvent col-12 col-md-6">
                    <LabelField title="Event Type" mandatory />
                    <Select
                      classNamePrefix="react-select"
                      formatOptionLabel={formatOptionLabel}
                      options={eventTypes}
                      styles={colorStyles}
                      isLoading={loadingEventTypes}
                      onChange={(val) =>
                        setFieldValue("eventType", {
                          id: val.value,
                          name: val.label,
                        })
                      }
                      value={{
                        label: values?.eventType?.name,
                        value: values?.eventType?.id,
                      }}
                      components={{ DropdownIndicator }}
                    />
                    {renderFormikError(formik, "eventType.id")}
                    <Box className="d-flex justify-content-between addEvent-switch align-items-center">
                      <LabelField title="Is this event confidential?" />
                      <SwitchComp
                        checked={values.isEventConfidencial}
                        handleChange={() =>
                          setFieldValue(
                            "isEventConfidencial",
                            !values.isEventConfidencial
                          )
                        }
                      />
                    </Box>
                  </Box>

                  <Box className="form-group pastoral-date-picker col-12 col-md-6">
                    <LabelField title="Event Date" mandatory />
                    <DatePicker
                      isClearable={values?.eventDate || false}
                      showIcon
                      placeholderText={placeholderFormat}
                      dateFormat={dateFormat}
                      selected={
                        values?.eventDate &&
                        convertUTCToLocalDate(values?.eventDate)
                      }
                      onChange={(date) => {
                        setFieldValue("eventDate", date);
                        convertLocalToUTCDate(date, "eventDate");
                      }}
                      customInput={
                        <MaskedInput
                          pipe={autoCorrectedDatePipe}
                          mask={dateMask}
                          keepCharPositions
                          guide
                        />
                      }
                    />
                    {renderFormikError(formik, "eventDate")}
                  </Box>

                  <Box className="form-group event-type-newEvent multi-select col-12 col-md-6">
                    <LabelField title="Notify To" />
                    <MultiSelect
                      isOptinal={false}
                      isCheckBoxOption={true}
                      options={notifyToUsers}
                      loading={loadingNotifyTo}
                      formikField="account[notifyTo]"
                      setOptions={() => {}}
                      variableForSave="title"
                      formik={formik}
                      handleFormikChange={handleFormikChange}
                      isAccount={true}
                      formikKey="notifyTo"
                      searchAction={() => {}}
                      isMultiple={true}
                      width="100%"
                      total={notifyToUsers.length}
                    />
                  </Box>

                  <Box className="form-group addEvent-description col-12">
                    <TextareaField
                      {...{
                        formik,
                        title: "Description",
                        name: "description",
                        placeholder: "Add memo",
                        style: { resize: "none" },
                        rows: 3,
                      }}
                      defaultValue={values?.description}
                    />
                    <Box className="d-flex justify-content-between addEvent-switch align-items-center">
                      <LabelField title="Is this description confidential?" />
                      <SwitchComp
                        checked={values.isDescriptionConfidencial}
                        handleChange={() =>
                          setFieldValue(
                            "isDescriptionConfidencial",
                            !values.isDescriptionConfidencial
                          )
                        }
                      />
                    </Box>
                  </Box>
                </Box>

                <Box className="d-flex justify-content-between mt-5 mb-5">
                  <MyButton
                    type="secondary"
                    content="Close"
                    onClick={closeModal}
                  />
                  <MyButton
                    type="primary"
                    content={
                      isSubmitting ? (
                        <CircularProgress size={20} style={{ color: "#fff" }} />
                      ) : (
                        "Save"
                      )
                    }
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    variant="outlined"
                    style={{ minWidth: 103 }}
                  />
                </Box>
              </form>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <NewEventSuccess
              eventType={formik.values?.eventType?.name}
              fullName={formik.values?.memberName?.name}
              closeModal={() => closeEditModal()}
              successEventData={successEventData}
              doNotGenerateHandler={doNotGenerateHandler}
              updateEventLoading={updateEventLoading}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewEvent;
